import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { CLIP } from '../../enums';
import { useClipFavourites } from './context';
import { useClipFilter } from '../ClipFilter';

export const useSidebarPlayback = ({ activeClip, seconds, playing, seeking, onTimelineEnded }: {
  activeClip?: CLIP;
  seconds: number;
  playing: boolean;
  seeking: boolean;
  onTimelineEnded?: (seconds: number) => void;
}) => {
  const { playbackClips, playbackList, actions, playerSeek, playerEnded } = useClipFavourites();
  const { dimmedIds } = useClipFilter();
  const isPlayable = useCallback((clip: CLIP) => !dimmedIds.has(String(clip.id)), [dimmedIds]);
  const enabled = playbackClips !== null;
  const previous = useRef({ id: activeClip?.id, seconds, playing });
  const previousList = useRef({ clips: playbackClips, list: playbackList });
  const seek = useCallback((clip: CLIP, play: boolean, time = clip.time_start) => {
    actions.current?.seek(clip, play, time);
    playerSeek.current?.(time);
  }, [actions, playerSeek]);

  useLayoutEffect(() => {
    playerEnded.current = (time) => {
      if (playbackClips === null) {
        onTimelineEnded?.(time);
        return;
      }
      if (!playbackClips?.length || !playing) return;
      const index = playbackClips.findIndex((clip) => clip.id === activeClip?.id);
      const active = playbackClips[index];
      if (!active || time < active.time_end - 0.05) return;
      const following = playbackClips.slice(index + 1).find(isPlayable);
      if (following) seek(following, true);
      else seek(active, false, active.time_end);
    };
    return () => { playerEnded.current = null; };
  }, [playbackClips, activeClip?.id, playing, playerEnded, seek, isPlayable, onTimelineEnded]);

  const next = useCallback(() => {
    if (!playbackClips) return false;
    if (!playbackClips.length) return true;
    const index = playbackClips.findIndex((clip) => clip.id === activeClip?.id);
    const target = playbackClips.slice(index + 1).find(isPlayable) || playbackClips.find(isPlayable);
    if (target) seek(target, playing);
    else actions.current?.pause();
    return true;
  }, [playbackClips, activeClip?.id, playing, seek, isPlayable, actions]);

  const previousClip = useCallback(() => {
    if (!playbackClips) return false;
    if (!playbackClips.length) return true;
    const index = playbackClips.findIndex((clip) => clip.id === activeClip?.id);
    const active = playbackClips[index];
    const target = active && isPlayable(active) && seconds > active.time_start + 0.5
      ? active : playbackClips.slice(0, Math.max(0, index)).reverse().find(isPlayable) || playbackClips.find(isPlayable);
    if (target) seek(target, playing);
    else actions.current?.pause();
    return true;
  }, [playbackClips, activeClip?.id, seconds, playing, seek, isPlayable, actions]);

  useEffect(() => {
    const last = previous.current;
    previous.current = { id: activeClip?.id, seconds, playing };
    const removedCurrentClip = playbackClips !== null && previousList.current.list === playbackList &&
      previousList.current.clips?.some((clip) => clip.id === activeClip?.id) &&
      !playbackClips.some((clip) => clip.id === activeClip?.id);
    previousList.current = { clips: playbackClips, list: playbackList };
    if (removedCurrentClip && playing) {
      actions.current?.pause();
      return;
    }
    if (!playbackClips?.length || !playing || seeking) return;
    const index = playbackClips.findIndex((clip) => clip.id === activeClip?.id);
    const active = playbackClips[index];
    const resumed = !last.playing;
    const following = playbackClips.slice(index + 1).find(isPlayable);
    if (resumed && (!active || (!following && seconds >= active.time_end))) {
      const first = playbackClips.find(isPlayable);
      if (first) seek(first, true);
      else actions.current?.pause();
      return;
    }
    if (!active) return;
    if (resumed && seconds < active.time_start) {
      seek(active, true);
      return;
    }
    const crossedEnd = last.id === active.id && last.seconds < active.time_end && seconds >= active.time_end;
    if ((crossedEnd || resumed) && seconds >= active.time_end) {
      if (following) seek(following, true);
      else seek(active, false, active.time_end);
    }
  }, [playbackClips, playbackList, activeClip?.id, seconds, playing, seeking, seek, actions, isPlayable]);

  return { enabled, next, previous: previousClip };
};
